import { useFormik } from 'formik'
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import {
  Button,
  FetchState,
  FormDateInput,
  FormInput,
  FormSelect,
  FormTextArea,
  FormTimeInput,
  IOption,
  ListLoading,
  useModal,
} from '../../../../../../components'
import {
  ButtonType,
  formatDateToPtBr,
  OptionsHelper,
  ParametroType,
  showAlertLoading,
  showError,
} from '../../../../../../../_metronic/helpers'
import { gerarDocumentoSchema } from '../../validation/documentoSchema'
import { PrescricaoDtoAtestado, PrescricaoView, TipoAtestadoView } from '../../core/_model'
import { AtendimentoView } from '../../../../atendimento/core/_models'
import {
  createAtestado,
  deletePrescricao,
  getDocumentoById,
  updateAtestado,
} from '../../core/_requests'

import { usePrescricaoUtils } from '../../utils/usePrescricaoUtils'

export type DocumentoModalRef = {
  onClose: () => void
}

export type IDocumentoModal = {
  onConfirm?: (geradas: PrescricaoView[]) => void
  onlyRetorno?: boolean
}

export const DocumentoModal = forwardRef<DocumentoModalRef, IDocumentoModal>(
  ({ onConfirm, onlyRetorno }, ref) => {
    useImperativeHandle(ref, () => ({
      onClose: handleDelete,
    }))

    const { fetchStates } = useModal()

    const { data: atendimento } = (fetchStates['atendimento'] || {}) as FetchState<AtendimentoView>
    const { handleSignPrescriptions } = usePrescricaoUtils(atendimento)

    const [prescricao, setPrescricao] = useState<PrescricaoView>()
    const [documento, setDocumento] = useState<TipoAtestadoView>()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [cidsOptions] = useState(
      OptionsHelper.createOptions(atendimento?.cids || [], 'cid.codigo', 'cid.codigo')
    )

    const initialValues = {
      texto: '',
      parametros: (documento?.parametros || []).map((p) => ({
        ...p,
        value: '' as string | { label: string; value: number },
      })),
      dataAtestado: '',
      isEncaminhamento: false,
      motivo: '',
      cid: prescricao?.atestado?.cid || '',
      documento: undefined as IOption<number> | undefined,
    }

    const formik = useFormik({
      initialValues,
      validationSchema: gerarDocumentoSchema(!!prescricao, cidsOptions.length > 0),
      onSubmit: async (values, { setSubmitting }) => {
        setSubmitting(true)

        try {
          const cidValue =
            typeof values.cid === 'string'
              ? values.cid
              : (values.cid as { label: string; value: string })?.value
          if (prescricao) {
            showAlertLoading()
            const atest: PrescricaoDtoAtestado = {
              tipoAtestado: prescricao.atestado?.tipo?.id as number,
              texto: values.texto,
              cid: cidValue || undefined,
              motivo: prescricao.atestado?.motivo,
            }
            await updateAtestado(prescricao.id, atest)

            const geradas = await handleSignPrescriptions([prescricao.id])
            if (geradas) onConfirm?.(geradas)
          } else {
            const atest: PrescricaoDtoAtestado = {
              tipoAtestado: documento?.id as number,
              data: values.dataAtestado,
              cid: cidValue || undefined,
              motivo: values.motivo || undefined,
              parametros: values.parametros.map((parametro) => ({
                nome: parametro.nome,
                valor:
                  parametro.tipoParametro === ParametroType.Date
                    ? formatDateToPtBr(parametro.value as string)
                    : parametro.tipoParametro === ParametroType.Especialidade
                    ? String((parametro.value as { label: string; value: number })?.label)
                    : String(parametro.value),
                tipo: parametro.tipoParametro,
              })),
            }
            const prescription = (await createAtestado(atendimento?.id as number, atest))[0]
            formik.setFieldValue('texto', prescription.atestado?.texto || '')
            setPrescricao(prescription)
          }
        } catch (error) {
          showError(error)
        } finally {
          setSubmitting(false)
        }
      },
    })

    const { setFieldValue } = formik
    const documentoId = formik.values.documento?.value

    useEffect(() => {
      if (!documentoId) return

      const getDocumento = async () => {
        setIsLoading(true)
        try {
          const doc = await getDocumentoById(documentoId)
          setDocumento(doc)

          setFieldValue('isEncaminhamento', doc.encaminhamento)
          setFieldValue(
            'parametros',
            (doc.parametros || []).map((p) => ({
              ...p,
              value: '',
            }))
          )
        } catch (error) {
          showError(error)
        } finally {
          setIsLoading(false)
        }
      }

      getDocumento()
    }, [documentoId, setFieldValue])

    const handleDelete = async () => {
      if (prescricao?.id) {
        try {
          await deletePrescricao(prescricao?.id)
          setPrescricao(undefined)
        } catch (error) {
          showError(error)
        }
      }
    }

    const notAtestado = formik.values.documento?.value !== 12 && documento?.atestado

    const documentosParams = useMemo(() => ({ onlyRetorno }), [onlyRetorno])

    return (
      <form onSubmit={formik.handleSubmit} noValidate>
        <FormSelect
          label='Documento'
          isRequired
          fetchOptions={OptionsHelper.getOptionsDocumentos}
          formik={formik}
          name='documento'
          className='mb-3'
          isDisabled={isLoading || !!prescricao}
          fetchOptionsParams={documentosParams}
        />
        {prescricao && (
          <FormTextArea
            formik={formik}
            name='texto'
            placeholder='Digite o texto'
            label='Prévia do Atestado'
            isRequired
            isDisabled={isLoading}
            className='mb-3'
          />
        )}
        {!prescricao && formik.values.documento && notAtestado && (
          <FormDateInput
            formik={formik}
            name='dataAtestado'
            label='Data do Atestado'
            placeholder='Digite'
            className='mb-3'
            isRequired
            isDisabled={isLoading}
          />
        )}

        {!prescricao && formik.values.documento && (
          <>
            {formik.values.parametros.map((parametro, index) => {
              if (parametro.tipoParametro === ParametroType.Integer) {
                return (
                  <FormInput
                    key={parametro.nome}
                    type='number'
                    label={parametro.rotulo}
                    name={`parametros[${index}].value`}
                    placeholder={`Digite ${parametro.rotulo.toLowerCase()}`}
                    formik={formik}
                    isRequired
                    className='mb-3'
                    isDisabled={isLoading}
                  />
                )
              } else if (parametro.tipoParametro === ParametroType.Date) {
                return (
                  <FormDateInput
                    key={parametro.nome}
                    label={parametro.rotulo}
                    name={`parametros[${index}].value`}
                    placeholder={`Digite ${parametro.rotulo.toLowerCase()}`}
                    formik={formik}
                    isRequired
                    className='mb-3'
                    isDisabled={isLoading}
                  />
                )
              } else if (parametro.tipoParametro === ParametroType.Time) {
                return (
                  <FormTimeInput
                    key={parametro.nome}
                    label={parametro.rotulo}
                    name={`parametros[${index}].value`}
                    placeholder={`Digite ${parametro.rotulo.toLowerCase()}`}
                    formik={formik}
                    isRequired
                    className='mb-3'
                    isDisabled={isLoading}
                  />
                )
              } else if (parametro.tipoParametro === ParametroType.Especialidade) {
                return (
                  <FormSelect
                    key={parametro.nome}
                    label={parametro.rotulo}
                    name={`parametros[${index}].value`}
                    placeholder={`Selecione uma ${parametro.rotulo.toLowerCase()}`}
                    fetchOptions={OptionsHelper.getOptionsEspecialidades}
                    formik={formik}
                    isRequired
                    isClearable={false}
                    className='mb-3'
                    isDisabled={isLoading}
                  />
                )
              }
              return null
            })}
          </>
        )}
        {formik.values.isEncaminhamento && !prescricao?.atestado?.motivo && (
          <FormTextArea
            formik={formik}
            label='Motivo'
            placeholder='Digite o motivo'
            name='motivo'
            className='mb-3'
            isRequired
          />
        )}

        {formik.values.documento &&
          (cidsOptions.length > 0 ? (
            <FormSelect
              formik={formik}
              name='cid'
              label='Classificação Internacional de Doenças - CID'
              placeholder='Selecione um CID'
              options={cidsOptions}
              className='mb-3'
            />
          ) : (
            <FormInput
              formik={formik}
              label='Classificação Internacional de Doenças - CID'
              placeholder='Digite o CID'
              name='cid'
              className='mb-3'
            />
          ))}

        <div className='d-flex justify-content-end mt-5'>
          {prescricao ? (
            <Button type={ButtonType.Submit} className='btn-info active me-2'>
              Assinar
            </Button>
          ) : (
            <Button type={ButtonType.Submit} className='btn-primary'>
              Criar
            </Button>
          )}
        </div>
        {isLoading && <ListLoading />}
      </form>
    )
  }
)
